import React, { useEffect, useState, useCallback } from "react";
import { authFetch, C } from "./App";

const SEV_COLOR = { severe: "#B71C1C", moderate: "#E65100", mild: "#1B5E20" };
const API_ORIGIN = "https://api.mysafeghana.com";

export function FloodsView({ token }) {
  const [floods, setFloods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all|verified|unverified
  const [search, setSearch] = useState("");
  const [err, setErr] = useState("");
  const [actingId, setActingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const q = filter === "verified" ? "?verified=true" : filter === "unverified" ? "?verified=false" : "";
    authFetch(`/admin/floods${q}`, token)
      .then((d) => setFloods(d.floods || []))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  function verify(id) {
    setActingId(id); setErr("");
    authFetch(`/admin/floods/${id}`, token, { method: "PATCH", body: JSON.stringify({ verified: true }) })
      .then(() => load())
      .catch((e) => setErr(e.message))
      .finally(() => setActingId(null));
  }

  function del(id) {
    if (!window.confirm("Permanently delete this flood report? This also removes its uploaded video, if any. This cannot be undone.")) return;
    setActingId(id); setErr("");
    authFetch(`/admin/floods/${id}`, token, { method: "DELETE" })
      .then(() => {
        setFloods((prev) => prev.filter((f) => f.id !== id));
        if (expandedId === id) setExpandedId(null);
      })
      .catch((e) => setErr(e.message))
      .finally(() => setActingId(null));
  }

  const visible = floods.filter((f) =>
    !search || (f.area || "").toLowerCase().includes(search.toLowerCase()) || (f.description || "").toLowerCase().includes(search.toLowerCase())
  );

  const withVideo = floods.filter((f) => f.video_url).length;

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111", margin: "0 0 4px" }}>Flood Report Moderation</h1>
      <p style={{ fontSize: 13, color: C.gray, margin: "0 0 20px" }}>
        Verify or remove citizen-submitted flood reports · {floods.length} total, {withVideo} with video evidence
      </p>

      {err && <div style={{ color: C.danger, fontSize: 13, marginBottom: 14 }}>{err}</div>}

      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <input
          value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by area or description…"
          style={{ flex: "1 1 220px", padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13 }}
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13 }}>
          <option value="all">All reports</option>
          <option value="unverified">Unverified</option>
          <option value="verified">Verified</option>
        </select>
      </div>

      {loading ? (
        <div style={{ color: C.gray, fontSize: 13 }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={{ color: C.gray, fontSize: 13, padding: 30, textAlign: "center", background: C.white, borderRadius: 12, border: `1px solid ${C.border}` }}>No flood reports match this filter.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visible.map((f) => {
            const sc = SEV_COLOR[f.severity] || "#607D8B";
            const expanded = expandedId === f.id;
            return (
              <div key={f.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
                <div
                  onClick={() => setExpandedId(expanded ? null : f.id)}
                  style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }}
                >
                  <div style={{ width: 4, height: 38, background: sc, borderRadius: 2, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{f.area || "Flood report"}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, color: sc, background: sc + "1A", padding: "1px 7px", borderRadius: 8 }}>{(f.severity || "").toUpperCase()}</span>
                      {f.verified
                        ? <span style={{ fontSize: 10, fontWeight: 600, color: C.success, background: "#E8F5E9", padding: "1px 7px", borderRadius: 8 }}>Verified</span>
                        : <span style={{ fontSize: 10, fontWeight: 600, color: "#B8860B", background: "#FFF8E1", padding: "1px 7px", borderRadius: 8 }}>Unverified</span>}
                      {f.video_url && <span style={{ fontSize: 10, fontWeight: 600, color: "#7C3AED", background: "#F3E8FF", padding: "1px 7px", borderRadius: 8 }}>🎬 Video</span>}
                    </div>
                    <div style={{ fontSize: 12, color: C.gray, textTransform: "capitalize" }}>
                      {(f.region || "").replace(/-/g, " ")} · {f.created_at ? new Date(f.created_at).toLocaleDateString() : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    {!f.verified && <ActBtn onClick={() => verify(f.id)} disabled={actingId === f.id} color={C.success}>Verify</ActBtn>}
                    <ActBtn onClick={() => del(f.id)} disabled={actingId === f.id} color={C.danger}>Delete</ActBtn>
                  </div>
                </div>

                {expanded && (
                  <div style={{ padding: "0 16px 16px 34px", borderTop: `1px solid ${C.border}` }}>
                    {f.video_url ? (
                      <video
                        src={`${API_ORIGIN}${f.video_url}`}
                        controls
                        style={{ width: "100%", maxWidth: 480, borderRadius: 8, background: "#000", marginTop: 14, display: "block" }}
                      />
                    ) : (
                      <div style={{ marginTop: 14, color: C.gray, fontSize: 12 }}>No video attached to this report.</div>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginTop: 14 }}>
                      {[
                        ["Ghana Post", f.ghana_post || "—"],
                        ["Water Depth", f.depth || "—"],
                        ["Duration", f.duration || "Ongoing"],
                        ["Reporter", f.user_name || "Anonymous"],
                        ["Coordinates", f.lat && f.lng ? `${Number(f.lat).toFixed(4)}, ${Number(f.lng).toFixed(4)}` : "—"],
                        ["Upvotes", `${f.upvotes || 0} community reports`],
                      ].map(([l, v]) => (
                        <div key={l} style={{ background: C.grayLight, borderRadius: 8, padding: "8px 10px" }}>
                          <div style={{ fontSize: 9, color: C.gray, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>{l}</div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#111" }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {f.description && (
                      <div style={{ marginTop: 10, background: C.grayLight, borderRadius: 8, padding: "10px 12px" }}>
                        <div style={{ fontSize: 9, color: C.gray, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Description</div>
                        <div style={{ fontSize: 12.5, color: "#333", lineHeight: 1.6 }}>{f.description}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ActBtn({ onClick, disabled, color, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ background: "transparent", border: `1px solid ${color}55`, color, borderRadius: 7, padding: "6px 11px", fontSize: 11.5, fontWeight: 600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1 }}>
      {children}
    </button>
  );
}
